import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "../ui/Skeleton.jsx";
import StatusBadge from "./StatusBadge.jsx";
import RoleBadge from "./RoleBadge.jsx";
import UserDetailModal from "./UserDetailModal.jsx";
import { formatLastActivity, formatName } from "../../utils/adminUserFormat.js";

const FILTER_OPTIONS = [
  { id: "active", label: "Active" },
  { id: "suspended", label: "Suspended" },
  { id: "deactivated", label: "Deactivated" },
  { id: "all", label: "All" },
];

function matchesFilter(user, statusFilter) {
  const status = String(user?.status || "").toLowerCase();
  if (statusFilter === "all") return true;
  if (statusFilter === "active") return status === "active";
  if (statusFilter === "suspended") return status === "suspended";
  if (statusFilter === "deactivated") return status === "deactivated";
  return status !== "deleted";
}

function FilterChip({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "6px 12px",
        fontSize: "13px",
        fontWeight: active ? 600 : 500,
        borderRadius: "999px",
        border: `1px solid ${active ? "var(--accent)" : "var(--border-bright)"}`,
        background: active ? "var(--accent-dim)" : "var(--bg-elevated)",
        color: active ? "var(--accent)" : "var(--text-secondary)",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );
}

function TableSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "16px" }}>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={40} borderRadius={6} />
      ))}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-secondary)",
  borderBottom: "1px solid var(--border)",
};

const tdStyle = {
  padding: "12px",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

export default function UserListTable({ refreshNonce = 0 }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const returnFocusRef = useRef(null);

  const reloadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/users?include_deleted=true", { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to load users (${res.status})`);
      }
      const data = await res.json();
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (err) {
      setUsers([]);
      setError(err?.message || "Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadUsers();
  }, [reloadUsers, refreshNonce]);

  const filteredUsers = useMemo(
    () => users.filter((u) => matchesFilter(u, statusFilter)),
    [users, statusFilter],
  );

  function openUser(user, rowEl) {
    returnFocusRef.current = rowEl;
    setSelectedUserId(user.id);
  }

  function handleRowKeyDown(e, user) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openUser(user, e.currentTarget);
    }
  }

  function handleUserChanged() {
    reloadUsers();
  }

  if (loading) return <TableSkeleton />;

  if (error) {
    return (
      <p
        role="alert"
        style={{
          marginTop: "16px",
          padding: "12px 14px",
          borderRadius: "6px",
          fontSize: "14px",
          background: "var(--critical-dim)",
          color: "var(--critical)",
          border: "1px solid var(--critical)",
        }}
      >
        {error}
      </p>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "4px" }}>
        {FILTER_OPTIONS.map((opt) => (
          <FilterChip
            key={opt.id}
            label={opt.label}
            active={statusFilter === opt.id}
            onClick={() => setStatusFilter((prev) => (prev === opt.id ? null : opt.id))}
          />
        ))}
      </div>

      {filteredUsers.length === 0 ? (
        <p className="text-secondary" style={{ fontSize: "14px", marginTop: "20px" }}>
          {users.length === 0 ? "No users found." : "No users match this filter."}
        </p>
      ) : (
        <div style={{ marginTop: "16px", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "720px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Last Activity</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Projects</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const deleted = String(user.status || "").toLowerCase() === "deleted";
                const rowStyle = deleted
                  ? { textDecoration: "line-through", opacity: 0.65 }
                  : undefined;
                return (
                  <tr
                    key={user.id}
                    tabIndex={0}
                    onClick={(e) => openUser(user, e.currentTarget)}
                    onKeyDown={(e) => handleRowKeyDown(e, user)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--bg-elevated)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "";
                    }}
                  >
                    <td style={{ ...tdStyle, ...rowStyle }}>{formatName(user.name)}</td>
                    <td style={{ ...tdStyle, ...rowStyle }}>{user.email || "—"}</td>
                    <td style={tdStyle}>
                      <RoleBadge user={user} />
                    </td>
                    <td style={tdStyle}>
                      <StatusBadge status={user.status} />
                    </td>
                    <td style={{ ...tdStyle, ...rowStyle }}>{formatLastActivity(user.last_activity)}</td>
                    <td style={{ ...tdStyle, textAlign: "right", ...rowStyle }}>
                      {Number(user.total_projects_count) || 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedUserId ? (
        <UserDetailModal
          userId={selectedUserId}
          returnFocusRef={returnFocusRef}
          onClose={() => setSelectedUserId(null)}
          onUserChanged={handleUserChanged}
        />
      ) : null}
    </div>
  );
}
